<template>
  <div :class="containerClass">
    <h1 v-if="!hideHeader" class="text-center font-serif text-3xl mb-1">Explore FOREVER<sub class="reg-mark">&reg;</sub> Facial Recognition</h1>
    <p v-if="!hideHeader" class="mb-7 text-center text-xl text-gray-600">
      Easily discover and organize the people in your photos, making it simple to relive your cherished moments—less
      searching, more enjoying. Now with new AI Enhance, you can transform file and album descriptions into engaging
      stories. More premium features on the way!
    </p>

    <div v-if="pending" class="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
      <core-upsell-product-card-skeleton />
      <core-upsell-product-card-skeleton />
    </div>

    <div v-else-if="oneYearProduct && permanentProduct" class="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
      <core-upsell-product-card
        :product="oneYearProduct"
        category="Subscription"
        title="Annual Subscription"
        suffix=" USD/per year"
        description="Renew annually. Cancel anytime."
        :loading="isAddingToCart"
        @cta="addToCart"
      />
      <core-upsell-product-card
        :product="permanentProduct"
        category="Permanent"
        title="One-Time Investment"
        suffix=" USD"
        description="Pay once. Never pay again!"
        featured
        :loading="isAddingToCart"
        @cta="addToCart"
      />
    </div>

    <core-contextual-error-box v-else size="sm" class="border-2 border-gray-200 rounded-md" :action="retry">
      <template #heading>Unable to Load Facial Recognition Plans</template>
      <template #default>Please try again or contact support.</template>
    </core-contextual-error-box>

    <core-upsell-features
      v-if="oneYearProduct && permanentProduct"
      class="mt-8"
      :items="FACIAL_RECOGNITION_FEATURES"
    />

    <p class="mt-8 text-center">
      <u-link :to="EXTERNAL_URLS.facialRecognition" external class="text-sm text-gray-500 underline">Learn More about FOREVER Facial Recognition</u-link>
    </p>
  </div>
</template>

<script setup>
  import {EXTERNAL_URLS} from '~/constants/external-urls.js';

  const props = defineProps({
    containerClass: {
      type: String,
      default: 'pt-12 p-5 sm:p-12'
    },
    hideHeader: Boolean,
    upsellTracking: Object
  });

  const FACIAL_RECOGNITION_FEATURES = [
    {
      title: 'Find People Faster',
      description: 'Automatically organize photos by the people who matter most.',
      icon: 'i-ri-group-fill'
    },
    {
      title: 'Discover More Memories',
      description: 'Use AI to enrich stories, uncover details, and bring memories to life.',
      icon: 'i-ri-sparkling-fill'
    },
    {
      title: 'Built for Preservation',
      description: 'Private AI designed to help organize your memories—not monetize them.',
      icon: 'i-ri-lock-fill'
    }
  ];

  const isAddingToCart = ref(false);
  async function addToCart(product) {
    if (isAddingToCart.value) return;

    isAddingToCart.value = true;
    try {
      trackUpsellCta({
        tracking: props.upsellTracking,
        product
      });

      await usePricingApi().addToCart({
        productId: product.id,
        promotionCode: product.promotionCode
      });

      window.location = EXTERNAL_URLS.cart;
    } catch {
      useErrorToast().add('Unable to add to cart at this time. Please try again later.');
    } finally {
      isAddingToCart.value = false;
    }
  }

  const {
    products: mappedProducts,
    pending,
    retry
  } = usePricingGroup({
    groupSlug: 'facial-recognition-products',
    server: false
  });

  const oneYearProduct = computed(() =>
    mappedProducts.value?.find(product => product.sku === 'FACIAL-RECOG-YR-001')
  );
  const permanentProduct = computed(() =>
    mappedProducts.value?.find(product => product.sku === 'FACIAL-RECOG-LF-001')
  );

</script>
